<script setup>
import { toLocaleFormat } from '@/helpers/date-helpers'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { capitalize, formatStatusCardNumber, formatNumberDisplayByLang } from '@/helpers/formatters'
import { CARD_TIERS_ENUM } from '@/constants/loyalty'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'

const { t } = useI18n()
const { loyaltyCard, productTier, firstName, lastName, rewardsPoints, balances } =
  storeToRefs(useUserStore())

const tier = computed(() => productTier.value.toLowerCase())

const endDate = computed(() => toLocaleFormat(loyaltyCard.value?.endDate))

const cardNumber = computed(() => loyaltyCard.value?.cardNumber)

const formattedFullName = computed(() => {
  return `${capitalize(firstName.value)} ${lastName.value.toUpperCase()}`
})

const statusPoints = computed(() => balances.value.nbPointsEarnLast12Months)
const currentNights = computed(() => balances.value.currentNightsBalance)

const rootClass = 'nav-header'
const em = (element, modifier) => `${rootClass}__${element}` + (modifier ? `--${modifier}` : '')
</script>

<template>
  <div :class="rootClass">
    <p :class="em('name')" data-cs-mask>{{ formattedFullName }}</p>
    <p :class="em('number')" data-cs-mask>{{ formatStatusCardNumber(cardNumber || '') }}</p>
    <div :class="em('status-block')">
      <div :class="em('status-logo', tier)">
        <svg
          width="34"
          height="36"
          viewBox="0 0 34 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M33.4387 36H25.1795L19.1062 22.8033C17.2461 23.8152 15.8339 25.5703 14.3593 27.403C13.2056 28.837 12.0136 30.3185 10.5389 31.5287C8.82254 32.9364 6.82743 34.1829 4.33354 35.0335C3.20396 35.4147 1.2822 35.9133 0.563371 35.9867C0.255303 36.016 0.0499238 36.0013 0.00591405 35.8987C-0.0234258 35.8107 0.0499238 35.752 0.372662 35.5907C0.437927 35.5594 0.539429 35.5137 0.667577 35.456C1.25963 35.1893 2.42023 34.6666 3.20396 34.1243C4.39222 33.2884 5.06704 32.3939 5.12572 31.7046C5.06583 31.4924 4.93674 31.2317 4.78138 30.9179C4.09948 29.5406 2.91145 27.1411 4.84699 23.3312C5.18897 22.6534 5.51199 22.0537 5.81034 21.4999C6.25429 20.6758 6.64361 19.9531 6.95946 19.2251C7.56093 17.832 8.00102 15.867 8.14772 14.4005C8.16239 14.3125 8.17706 14.3125 8.22107 14.3712C9.38 15.5444 13.869 20.149 13.2088 24.739C14.7052 24.1671 17.2871 22.422 18.4754 21.4981C18.7546 21.2807 19.0122 21.0618 19.2586 20.8525C20.1257 20.1159 20.8539 19.4973 21.8934 19.4744C22.5612 19.4662 22.8862 19.6432 23.2119 19.8207C23.4659 19.959 23.7203 20.0976 24.1379 20.149C24.3727 20.1783 24.7247 20.1343 24.9008 20.0464C24.9741 20.017 24.9595 19.929 24.8421 19.8997C24.3049 19.7716 23.9354 19.514 23.5665 19.2568C23.0489 18.8959 22.5325 18.5359 21.556 18.5359C20.4302 18.5359 19.4705 19.3162 18.6956 19.9462C18.4406 20.1536 18.2056 20.3447 17.9912 20.4863L13.7516 11.6583C11.9912 7.96282 12.5194 5.27922 16.4216 0L33.4387 36Z"
            fill="white"
          />
        </svg>
      </div>
      <dl :class="em('status-info')">
        <div :class="em('status-info-item')">
          <dt>{{ t('navigation.loyalty.tier') }}</dt>
          <dd>{{ tier.toUpperCase() }}</dd>
        </div>
        <div
          v-if="tier !== CARD_TIERS_ENUM.CLASSIC.toLowerCase() && endDate"
          :class="em('status-info-item')"
        >
          <dt>{{ t('navigation.loyalty.until') }}</dt>
          <dd>{{ endDate }}</dd>
        </div>
      </dl>
    </div>
    <dl :class="em('points')">
      <div :class="[em('points-item'), em('point-status')]">
        <dt>{{ t('navigationBar.rewardPoints') }}</dt>
        <dd>{{ formatNumberDisplayByLang(rewardsPoints) }}</dd>
      </div>
      <div :class="em('points-item')">
        <dt>{{ t('navigationBar.statusPoints') }}</dt>
        <dd>{{ formatNumberDisplayByLang(statusPoints) }}</dd>
      </div>
      <div :class="em('points-item')">
        <dt>{{ t('navigationBar.nights') }}</dt>
        <dd>{{ formatNumberDisplayByLang(currentNights) }}</dd>
      </div>
    </dl>
  </div>
</template>

<style lang="scss">
$root: '.nav-header';

#{$root} {
  &__name {
    color: ccf.$color-neutral-90;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    @include ccf.ui-heading-02;
  }
  &__number {
    color: ccf.$color-text-muted;
    @include ccf.ui-label-01;
  }
  &__status-block {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: ccf.$spacing-inner-02;
    gap: ccf.$spacing-inner-04;
    background-color: rgba(118, 119, 122, 0.1);
    border-radius: ccf.$border-radius;
    margin: ccf.$spacing-outer-01 0;
  }
  &__status-logo {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ccf.$border-radius;
    &--classic {
      @extend #{$root}__status-logo;
      background-color: ccf.$color-status-classic;
    }
    &--silver {
      @extend #{$root}__status-logo;
      background-color: ccf.$color-status-silver;
    }
    &--gold {
      @extend #{$root}__status-logo;
      background-color: ccf.$color-status-gold;
    }
    &--platinum {
      @extend #{$root}__status-logo;
      background-color: ccf.$color-status-platinum;
    }
    &--diamond {
      @extend #{$root}__status-logo;
      background-color: ccf.$color-status-diamond;
    }
    &--limitless {
      @extend #{$root}__status-logo;
      background-color: ccf.$color-status-black;
    }
  }
  &__status-info {
    color: ccf.$color-text-muted;
    display: flex;
    flex-direction: column;
    @include ccf.ui-caption-01;
    &-item {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.5em;
      &:first-child {
        dt {
          font-weight: 500;
        }
        dd {
          color: ccf.$color-neutral-90;
          font-weight: 500;
          @include ccf.ui-body-01;
        }
      }
      &:nth-child(2) {
        font-style: italic;
      }
    }
  }
  &__points {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: ccf.$spacing-inner-02 ccf.$spacing-inner-05;
    @include ccf.ui-body-02;
    &-item {
      display: flex;
      flex-direction: row;
      gap: 0.5em;
      dt {
        font-weight: 400;
      }
      dd {
        color: ccf.$color-text-accent;
        font-weight: 500;
      }
    }
  }

  &__point-status {
    flex: 1 1 100%;
  }
}
</style>
